import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Newspaper, Layers, FolderOpen } from "lucide-react";
import api from "@/lib/apiClient";

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);
  useEffect(() => { if (!open) { setQ(""); setResults(null); } }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
      } catch {} finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  if (!open) return null;
  const go = (path) => { onClose(); navigate(path); };
  const total = results ? results.pils.length + results.documents.length + results.press.length + results.initiatives.length : 0;

  const Group = ({ icon: Icon, title, items, render }) =>
    items.length > 0 && (
      <div className="mb-5">
        <div className="flex items-center gap-2 label-eyebrow text-npst-gold mb-2 px-1">
          <Icon size={13} /> {title}
        </div>
        <div className="space-y-1">{items.map(render)}</div>
      </div>
    );

  return (
    <div className="fixed inset-0 z-[100] bg-npst-navyDeep/40 backdrop-blur-sm flex items-start justify-center pt-[8vh] px-4" onClick={onClose} data-testid="global-search-overlay">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl border border-npst-line overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 border-b border-npst-line">
          <Search size={18} className="text-npst-muted" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search PILs, case numbers, orders, media, documents…"
            className="flex-1 py-4 text-base outline-none bg-transparent text-npst-ink placeholder:text-npst-muted"
          />
          <button data-testid="global-search-close" onClick={onClose} className="text-npst-muted hover:text-npst-ink"><X size={18} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {loading && <p className="text-sm text-npst-muted px-1 py-4">Searching…</p>}
          {!loading && results && total === 0 && (
            <p className="text-sm text-npst-muted px-1 py-8 text-center" data-testid="search-no-results">No results for “{q}”. Try a different term.</p>
          )}
          {!loading && !results && <p className="text-sm text-npst-muted px-1 py-4">Type at least two characters to search the archive.</p>}
          {results && (
            <>
              <Group icon={FileText} title="Public Interest Litigations" items={results.pils} render={(p) => (
                <button key={p.id} onClick={() => go(`/pil/${p.pil_code.toLowerCase()}`)} className="block w-full text-left px-3 py-2 rounded hover:bg-npst-goldLight" data-testid={`search-pil-${p.pil_code}`}>
                  <div className="font-serif text-base text-npst-ink leading-snug">{p.title}</div>
                  <div className="text-xs text-npst-muted font-mono mt-0.5">{p.court} · {p.case_number}</div>
                </button>
              )} />
              <Group icon={Newspaper} title="Media & Press" items={results.press} render={(p) => (
                <button key={p.id} onClick={() => go(`/media/${p.slug}`)} className="block w-full text-left px-3 py-2 rounded hover:bg-npst-goldLight">
                  <div className="font-serif text-base text-npst-ink leading-snug">{p.headline}</div>
                  <div className="text-xs text-npst-muted mt-0.5">{p.category} · {p.publish_date}</div>
                </button>
              )} />
              <Group icon={FolderOpen} title="Documents & Resources" items={results.documents} render={(d) => (
                <button key={d.id} onClick={() => go(`/documents`)} className="block w-full text-left px-3 py-2 rounded hover:bg-npst-goldLight">
                  <div className="text-sm text-npst-ink">{d.title}</div>
                  <div className="text-xs text-npst-muted mt-0.5">{d.category}</div>
                </button>
              )} />
              <Group icon={Layers} title="Initiatives" items={results.initiatives} render={(i) => (
                <button key={i.id} onClick={() => go(`/initiatives/${i.slug}`)} className="block w-full text-left px-3 py-2 rounded hover:bg-npst-goldLight">
                  <div className="text-sm text-npst-ink">{i.title}</div>
                  <div className="text-xs text-npst-muted mt-0.5">{i.category}</div>
                </button>
              )} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
